import React, { useState, useRef, useEffect } from "react";
import { Edit, ImageNotSupported } from "@mui/icons-material";
import ImageCropper from "../../components/ImageCropperModal/ImageCropper.jsx";
import { toast } from "react-toastify";
import {
  addBottomBanner,
  getBottomBanner,
} from "../../services/dashboardService.js";
import { Link } from "react-router-dom";
import defaultBannerImage from "../../assets/images/default.jpg";
const BottomBanner = ({
  initialBanner = null,
  editable = true,
  aspectRatio = 16 / 6,
}) => {
  const bottomBannerFileInputRef = useRef(null);

  // Default banner data
  const defaultBanner = {
    title: "Upgrade to Premium",
    description: "Get unlimited access to all courses and premium features",
    image: defaultBannerImage,
    href: "#",
    gradientFrom: "#4CAF50",
    gradientTo: "#2196F3",
    textColor: "#FFFFFF",
  };

  // Bottom Banner state
  const [bottomBanner, setBottomBanner] = useState(
    initialBanner || defaultBanner
  );
  const [showCropper, setShowCropper] = useState(false);
  const [cropImage, setCropImage] = useState(null);
  const [showBottomBannerForm, setShowBottomBannerForm] = useState(false);
  const [bottomBannerFormData, setBottomBannerFormData] = useState({
    image: null,
    href: "",
    title: "",
    description: "",
    gradientFrom: "#4CAF50",
    gradientTo: "#2196F3",
    textColor: "#FFFFFF",
  });
  const [loading, setLoading] = useState(false);
  const [savingBottomBanner, setSavingBottomBanner] = useState(false);
  const [gradientToInput, setGradientToInput] = useState("#2196F3");
  const [gradientFromInput, setGradientFromInput] = useState("#4CAF50");
  const [inputText, setInputText] = useState("#FFFFFF");
  // Load initial banner or fetch from API
  useEffect(() => {
    if (initialBanner) {
      setBottomBanner(initialBanner);
    } else if (getBottomBanner) {
      handlegetBottomBanner();
    }
  }, [initialBanner]);

  const handlegetBottomBanner = async () => {
    if (!getBottomBanner) return;

    try {
      setLoading(true);
      const response = await getBottomBanner();
      if (response.success && response.data) {
        const bannerData = response.data.details;
        setBottomBanner({
          title: bannerData.title || defaultBanner.title,
          description: bannerData.description || defaultBanner.description,
          image: bannerData.url || "",
          href: bannerData.href || "#",
          gradientFrom: bannerData.gradientFrom || defaultBanner.gradientFrom,
          gradientTo: bannerData.gradientTo || defaultBanner.gradientTo,
          textColor: bannerData.textColor || defaultBanner.textColor,
        });
      }
    } catch (error) {
      console.error("Error fetching bottom banner:", error);
      toast.error("Failed to fetch bottom banner");
    } finally {
      setLoading(false);
    }
  };

  const handleBottomBannerFileChange = (event) => {
    const file = event.target.files[0];
    if (file && file.type.startsWith("image/")) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setCropImage(reader.result);
        setShowCropper(true);
      };
      reader.readAsDataURL(file);
    } else {
      toast.error("Please upload a valid image file.");
    }
    event.target.value = "";
  };

  const handleCropComplete = async (croppedBlob) => {
    const croppedUrl = URL.createObjectURL(croppedBlob);
    setBottomBannerFormData((prev) => ({ ...prev, image: croppedUrl }));
    setShowCropper(false);
  };

  const validateBottomBannerForm = () => {
    if (!bottomBannerFormData.image) {
      toast.error("Please select an image");
      return false;
    }
    if (!bottomBannerFormData.href) {
      toast.error("Please enter a link URL");
      return false;
    }
    if (!bottomBannerFormData.title) {
      toast.error("Please enter a title");
      return false;
    }
    if (!bottomBannerFormData.description) {
      toast.error("Please enter a description");
      return false;
    }
    return true;
  };

  const handleBottomBannerFormSubmit = async (e) => {
    e.preventDefault();

    if (!validateBottomBannerForm()) {
      return;
    }

    try {
      setSavingBottomBanner(true);

      if (addBottomBanner) {
        // Use API if provided
        const response = await fetch(bottomBannerFormData.image);
        const blob = await response.blob();
        const file = new File([blob], "bottom-banner.jpg", {
          type: "image/jpeg",
        });

        const formData = new FormData();
        formData.append("banner", file);
        formData.append("href", bottomBannerFormData.href);
        formData.append("title", bottomBannerFormData.title);
        formData.append("description", bottomBannerFormData.description);
        formData.append("gradientFrom", bottomBannerFormData.gradientFrom);
        formData.append("gradientTo", bottomBannerFormData.gradientTo);
        formData.append("textColor", bottomBannerFormData.textColor);

        const result = await addBottomBanner({ payload: formData });

        if (result.success) {
          if (getBottomBanner) {
            await handlegetBottomBanner();
          }
          toast.success(result.message || "Bottom banner updated successfully");
        }
      } else {
        // Handle local update if no API provided
        setBottomBanner({
          title: bottomBannerFormData.title,
          description: bottomBannerFormData.description,
          image: bottomBannerFormData.image,
          href: bottomBannerFormData.href,
          gradientFrom: bottomBannerFormData.gradientFrom,
          gradientTo: bottomBannerFormData.gradientTo,
          textColor: bottomBannerFormData.textColor,
        });
        toast.success("Bottom banner updated successfully");
      }

      setShowBottomBannerForm(false);
    } catch (error) {
      console.error("Error updating bottom banner:", error);
      toast.error(
        error.message || "Failed to update bottom banner. Please try again."
      );
    } finally {
      setSavingBottomBanner(false);
    }
  };

  const handleEditBottomBanner = () => {
    setBottomBannerFormData({
      image: bottomBanner.image,
      href: bottomBanner.href,
      title: bottomBanner.title,
      description: bottomBanner.description,
      gradientFrom: bottomBanner.gradientFrom,
      gradientTo: bottomBanner.gradientTo,
      textColor: bottomBanner.textColor,
    });
    setShowBottomBannerForm(true);
  };

  const handleBottomBannerFormCancel = () => {
    setBottomBannerFormData({
      image: null,
      href: "",
      title: "",
      description: "",
      gradientFrom: "#4CAF50",
      gradientTo: "#2196F3",
      textColor: "#FFFFFF",
    });
    setShowBottomBannerForm(false);
  };

  return (
    <>
      <div
        className="ad-banner-container"
        style={{
          background: `linear-gradient(135deg, ${bottomBanner.gradientFrom} 0%, ${bottomBanner.gradientTo} 100%)`,
        }}
      >
        {editable && (
          <button
            className="edit-bottom-banner-btn"
            onClick={handleEditBottomBanner}
          >
            <Edit />
          </button>
        )}

        <div
          className="ad-banner-content"
          style={{ color: bottomBanner.textColor }}
        >
          <h2>{bottomBanner.title}</h2>
          <p>{bottomBanner.description}</p>
          <Link
            to={bottomBanner.href}
            className="ad-banner-button"
            style={{
              backgroundColor: bottomBanner.textColor,
              color: bottomBanner.gradientFrom,
            }}
          >
            Learn More
          </Link>
        </div>
        <div className="ad-banner-image">
          {bottomBanner.image ? (
            <img
              src={bottomBanner.image}
              alt={bottomBanner.title}
              loading="lazy"
            />
          ) : (
            <div className="empty-image-container">
              <div className="empty-image-placeholder">
                <ImageNotSupported className="empty-image-icon" />
                <p>No image available</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Bottom Banner Form Modal */}
      {showBottomBannerForm && (
        <div className="banner-form-modal-overlay">
          <div className="bottom-banner-form-modal">
            <div className="header">
              <h5>Edit Advertisement Banner</h5>
            </div>

            <form
              onSubmit={handleBottomBannerFormSubmit}
              className="banner-form"
            >
              <div className="form-image-container">
                {bottomBannerFormData.image ? (
                  <img
                    src={bottomBannerFormData.image}
                    alt="Banner preview"
                    className="form-image-preview"
                    loading="lazy"
                  />
                ) : (
                  <div className="empty-image-container">
                    <div className="empty-image-placeholder">
                      <ImageNotSupported className="empty-image-icon" />
                      <p>No image selected</p>
                    </div>
                  </div>
                )}
              </div>
              <div className="upload-btn">
                <button
                  type="button"
                  className="form-image-upload-btn"
                  onClick={() => bottomBannerFileInputRef.current?.click()}
                  disabled={savingBottomBanner}
                >
                  Upload
                </button>
              </div>

              <div className="form-field">
                <label htmlFor="bottom-banner-title">Title</label>
                <input
                  id="bottom-banner-title"
                  type="text"
                  value={bottomBannerFormData.title}
                  onChange={(e) =>
                    setBottomBannerFormData({
                      ...bottomBannerFormData,
                      title: e.target.value,
                    })
                  }
                  placeholder="Enter banner title"
                  disabled={savingBottomBanner}
                />
              </div>

              <div className="form-field">
                <label htmlFor="bottom-banner-description">Description</label>
                <textarea
                  id="bottom-banner-description"
                  value={bottomBannerFormData.description}
                  onChange={(e) =>
                    setBottomBannerFormData({
                      ...bottomBannerFormData,
                      description: e.target.value,
                    })
                  }
                  placeholder="Enter banner description"
                  disabled={savingBottomBanner}
                />
              </div>

              <div className="form-field">
                <label htmlFor="bottom-banner-href">Link URL</label>
                <input
                  id="bottom-banner-href"
                  type="text"
                  value={bottomBannerFormData.href}
                  onChange={(e) =>
                    setBottomBannerFormData({
                      ...bottomBannerFormData,
                      href: e.target.value,
                    })
                  }
                  placeholder="Enter banner link URL"
                  disabled={savingBottomBanner}
                />
              </div>
              <div className="color-grid">
                <div className="form-field">
                  <label htmlFor="bottom-banner-gradient-from">
                    Gradient From Color
                  </label>
                  <div className="color-input-container">
                    <input
                      id="bottom-banner-gradient-from"
                      type="color"
                      value={bottomBannerFormData.gradientFrom}
                      onChange={(e)=>{
                        const value = e.target.value;
                        setBottomBannerFormData({
                          ...bottomBannerFormData,
                          gradientFrom: value
                        });
                        setGradientFromInput(value);
                      }}
                      disabled={savingBottomBanner}
                    />
                    <input
                      type="text"
                      value={gradientFromInput}
                      onChange={(e)=>{
                        const value = e.target.value;
                        setGradientFromInput(value);
                        {value.length === 7 ? setBottomBannerFormData({
                          ...bottomBannerFormData,
                          gradientFrom:value
                        }):"#4CAF50"}
                      }}
                      placeholder="Enter hex color code"
                      disabled={savingBottomBanner}
                    />
                  </div>
                </div>

                <div className="form-field">
                  <label htmlFor="bottom-banner-gradient-to">
                    Gradient To Color
                  </label>
                  <div className="color-input-container">
                    <input
                      id="bottom-banner-gradient-to"
                      type="color"
                      value={bottomBannerFormData.gradientTo}
                      onChange={(e)=>{
                        const value = e.target.value;
                        setBottomBannerFormData({
                          ...bottomBannerFormData,
                          gradientTo: value
                        })
                        setGradientToInput(value)
                      }}
                      disabled={savingBottomBanner}
                    />
                    <input
                      type="text"
                      value={gradientToInput}
                      onChange={(e)=>{
                        const value = e.target.value
                        setGradientToInput(value);
                        {value.length == 7 ? setBottomBannerFormData({
                          ...bottomBannerFormData,
                          gradientTo: value
                        }):"#2196F3"}
                      }}
                      placeholder="Enter hex color code"
                      disabled={savingBottomBanner}
                    />
                  </div>
                </div>

                <div className="form-field">
                  <label htmlFor="bottom-banner-text-color">Text Color</label>
                  <div className="color-input-container">
                    <input
                      id="bottom-banner-text-color"
                      type="color"
                      value={bottomBannerFormData.textColor}
                      onChange={(e)=>{
                        const value = e.target.value
                        setBottomBannerFormData({
                          ...bottomBannerFormData,
                          textColor: value
                        })
                        setInputText(value)
                      }}
                      disabled={savingBottomBanner}
                    />
                    <input
                      type="text"
                      value={inputText}
                      onChange={(e)=>{
                        const value = e.target.value
                        setInputText(value)
                        {value.length === 7 ? setBottomBannerFormData({
                          ...bottomBannerFormData,
                          textColor: value
                        }):""}
                      }}
                      placeholder="Enter hex color code"
                      disabled={savingBottomBanner}
                    />
                  </div>
                </div>
              </div>

              <div className="form-actions">
                <button
                  type="button"
                  className="cancel-btn"
                  onClick={handleBottomBannerFormCancel}
                  disabled={savingBottomBanner}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className={`save-btn ${savingBottomBanner ? "saving" : ""}`}
                  disabled={!bottomBannerFormData.image || savingBottomBanner}
                >
                  {savingBottomBanner ? "Saving..." : "Save Banner"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Image Cropper Modal */}
      {showCropper && (
        <div className="cropper-modal-overlay">
          <div className="cropper-modal-content">
            <ImageCropper
              open={showCropper}
              onClose={() => setShowCropper(false)}
              imageSrc={cropImage}
              onCropComplete={handleCropComplete}
              aspectRatio={aspectRatio}
              title="Banner Image Cropper"
            />
          </div>
        </div>
      )}

      <input
        type="file"
        ref={bottomBannerFileInputRef}
        style={{ display: "none" }}
        accept="image/*"
        onChange={handleBottomBannerFileChange}
      />
    </>
  );
};

export default BottomBanner;
